// axiosConfig.ts

import axios, { AxiosError, AxiosInstance } from 'axios';
import { redirectToLogin } from './authRedirect';
import {
  getGlobalApiToastMessage,
  isNetworkOrUnreachableError,
} from './apiErrorMessage';
import { notifyApiUser } from './apiNotification';
import { attachRequestContext } from './attachRequestContext';

const isLocalApi =
  process.env.REACT_APP_API_URL?.includes('localhost') === true ||
  process.env.REACT_APP_API_URL?.includes('127.0.0.1') === true;

const REQUEST_TIMEOUT_MS = isLocalApi ? 30_000 : 12_000;

const OFFLINE_MESSAGE =
  'You appear to be offline. Check your internet connection.';

const attachOfflineRequestGuard = (apiInstance: AxiosInstance): void => {
  apiInstance.interceptors.request.use((config) => {
    if (
      typeof navigator !== 'undefined' &&
      !navigator.onLine &&
      !config.skipGlobalErrorToast
    ) {
      notifyApiUser(OFFLINE_MESSAGE);
      return Promise.reject(
        new AxiosError('Offline', AxiosError.ERR_NETWORK, config),
      );
    }
    return config;
  });
};

const attachAuthRedirect = (apiInstance: AxiosInstance): void => {
  apiInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (
        error.response?.status === 401 &&
        !error.config?.skipAuthRedirect
      ) {
        redirectToLogin();
      }
      return Promise.reject(error);
    },
  );
};

const attachGlobalErrorToast = (apiInstance: AxiosInstance): void => {
  apiInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (
        error.response?.status === 401 ||
        error.config?.skipGlobalErrorToast ||
        !isNetworkOrUnreachableError(error)
      ) {
        return Promise.reject(error);
      }
      notifyApiUser(getGlobalApiToastMessage(error));
      return Promise.reject(error);
    },
  );
};

const getApiInstance = (apiUrl: string, settings: string): AxiosInstance => {
  const apiInstance = axios.create({
    withCredentials: true,
    baseURL: process.env.REACT_APP_API_URL + apiUrl,
    timeout: REQUEST_TIMEOUT_MS,
  });

  apiInstance.defaults.headers.common['settings'] = settings;
  attachRequestContext(apiInstance);
  attachOfflineRequestGuard(apiInstance);
  attachAuthRedirect(apiInstance);
  attachGlobalErrorToast(apiInstance);

  return apiInstance;
};

export default getApiInstance;
